import React, {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Typography,
} from "@sumprio/ui";
import EmptyState from "../EmptyState";
import TablePaginationPlaceholder from "../TablePaginationPlaceholder";
import { TableSkeletonRows, listTableSkeletonRowCount } from "../TableSkeleton/TableSkeletonBody";
import ListTablePagination from "../ListTablePagination/ListTablePagination";
import { ListTableScrollBoundaryRefContext } from "../ListTableScrollBoundaryContext";
import { ListTableScrollRegion } from "../ListTableScrollRegion";
import { getListTablePaginationDisplay } from "utils/listTablePagination";
import {
  defaultDataTableGetSortValue,
  getDataTableSortArrow,
  getNextDataTableSort,
  sortItemsByColumn,
  type DataTableGetSortValue,
  type DataTableSortDirection,
} from "utils/dataTableSort";
import "./DataTable.scss";

export type { DataTableSortDirection };

export interface DataTableColumn {
  id: string;
  label: string;
  actionColumn?: boolean;
  sortable?: boolean;
  headerCellProps?: Partial<ComponentProps<typeof TableHeaderCell>>;
}

export const DATA_TABLE_ACTION_COLUMN_CELL_PROPS: Pick<
  ComponentProps<typeof TableCell>,
  "width" | "contentStyle" | "align"
> = {
  width: "1%",
  contentStyle: "action-icons",
  align: "center",
};

export const DATA_TABLE_ACTION_HEADER_CELL_PROPS: Pick<
  ComponentProps<typeof TableHeaderCell>,
  "actionColumn" | "width" | "wrap"
> & { style: React.CSSProperties } = {
  actionColumn: true,
  width: "1%",
  wrap: false,
  style: { whiteSpace: "nowrap", width: "1%" },
};

export interface DataTableCheckboxEvent {
  type: "selectAll" | "row";
  rowId?: string | number;
  checked: boolean;
}

export interface DataTableProps<T> {
  columns: DataTableColumn[];
  items: T[];
  getRowId: (_item: T) => string | number;
  selectedIds: Set<string | number>;
  selectableRowIds?: Set<string | number>;
  onCheckbox: (_event: DataTableCheckboxEvent) => void;
  renderRow: (_item: T, _selected: boolean) => React.ReactNode;
  showCheckbox?: boolean;
  page: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (_page: number) => void;
  onPageSizeChange: (_pageSize: number) => void;
  pageSizeOptions?: number[];
  hasSearchQuery?: boolean;
  searchEmptyState?: React.ReactNode;
  isSearchLoading?: boolean;
  searchFetchComplete?: boolean;
  size?: "xs" | "sm" | "md";
  emptyStateWhenNoData?: React.ReactNode;
  alwaysShowEmptyStateWhenNoData?: boolean;
  isPageLoading?: boolean;
  isTableRefreshing?: boolean;
  listStylePagination?: boolean;
  pageSizeTriggerLabel?: string;
  selectedPageSizeChoice?: number;
  itemsRangeOverride?: { start: number; end: number };
  hidePageNavigation?: boolean;
  showScrollForMoreHint?: boolean;
  onLoadMore?: () => void;
  loadMoreEnabled?: boolean;
  isLoadingMore?: boolean;
  stickyHeader?: boolean;
  stickyFirstColumn?: boolean;
  stickyActionColumn?: boolean;
  scrollMaxHeight?: string | number;
  fillViewportHeight?: boolean;
  enableSort?: boolean;
  getSortValue?: DataTableGetSortValue<T>;
  sortColumnId?: string | null;
  sortDirection?: DataTableSortDirection;
  onSortColumn?: (_columnId: string) => void;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50];

export const LIST_TABLE_STICKY_BODY_MAX_HEIGHT = "min(70vh, 720px)";

export function DataTable<T>({
  columns,
  items,
  getRowId,
  selectedIds,
  selectableRowIds,
  onCheckbox,
  renderRow,
  showCheckbox = true,
  page,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  hasSearchQuery = false,
  searchEmptyState,
  isSearchLoading = false,
  searchFetchComplete = true,
  size = "xs",
  emptyStateWhenNoData,
  alwaysShowEmptyStateWhenNoData = false,
  isPageLoading = false,
  isTableRefreshing = false,
  listStylePagination = false,
  pageSizeTriggerLabel,
  selectedPageSizeChoice,
  itemsRangeOverride,
  hidePageNavigation = false,
  showScrollForMoreHint = false,
  onLoadMore,
  loadMoreEnabled = false,
  isLoadingMore = false,
  stickyHeader = true,
  stickyFirstColumn,
  stickyActionColumn,
  scrollMaxHeight = LIST_TABLE_STICKY_BODY_MAX_HEIGHT,
  fillViewportHeight = true,
  enableSort = true,
  getSortValue,
  sortColumnId: controlledSortColumnId = null,
  sortDirection: controlledSortDirection,
  onSortColumn: controlledOnSortColumn,
}: DataTableProps<T>) {
  const [internalSortColumnId, setInternalSortColumnId] = useState<string | null>(null);
  const [internalSortDirection, setInternalSortDirection] = useState<DataTableSortDirection>("asc");

  const isControlledSort = controlledOnSortColumn !== undefined;
  const sortColumnId = isControlledSort ? controlledSortColumnId : internalSortColumnId;
  const sortDirection = isControlledSort
    ? (controlledSortDirection ?? "asc")
    : internalSortDirection;

  const handleSortColumn = useCallback(
    (columnId: string) => {
      if (isControlledSort) {
        controlledOnSortColumn(columnId);
        return;
      }
      const next = getNextDataTableSort(internalSortColumnId, columnId, internalSortDirection);
      setInternalSortColumnId(next.columnId);
      setInternalSortDirection(next.direction);
    },
    [controlledOnSortColumn, internalSortColumnId, internalSortDirection, isControlledSort]
  );

  const resolveSortValue = getSortValue ?? defaultDataTableGetSortValue<T>;

  const displayItems = useMemo(() => {
    if (!enableSort || isControlledSort || !sortColumnId) {
      return items;
    }
    return sortItemsByColumn(items, sortColumnId, sortDirection, resolveSortValue);
  }, [enableSort, isControlledSort, items, resolveSortValue, sortColumnId, sortDirection]);

  const isColumnSortable = useCallback(
    (col: DataTableColumn) => enableSort && !col.actionColumn && col.sortable !== false,
    [enableSort]
  );

  const colCount = columns.length + (showCheckbox ? 1 : 0);
  const pageIds = displayItems.map(getRowId).filter((id) => id !== undefined && id !== null);
  const effectiveSelectableIds = selectableRowIds
    ? pageIds.filter((id) => selectableRowIds.has(id))
    : pageIds;
  const allSelected =
    effectiveSelectableIds.length > 0 && effectiveSelectableIds.every((id) => selectedIds.has(id));
  const someSelected = effectiveSelectableIds.some((id) => selectedIds.has(id)) && !allSelected;

  const showSearchEmpty =
    displayItems.length === 0 &&
    (alwaysShowEmptyStateWhenNoData || hasSearchQuery || !!emptyStateWhenNoData);
  const showSearching = showSearchEmpty && (isSearchLoading || !searchFetchComplete);
  const showSkeletonInitial = isPageLoading && displayItems.length === 0;
  const showSkeletonBody = showSkeletonInitial || showSearching;
  const showPagination = !showSearchEmpty && !showSkeletonBody && totalItems > 0;
  const reservePaginationSlot = showSkeletonBody && !showSearchEmpty;
  const skeletonRowCount = listTableSkeletonRowCount(pageSize);
  const currentPage = page + 1;
  const dataColumnCount = columns.length;
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const listTableScrollBoundaryRef = useRef<HTMLDivElement | null>(null);
  const listPaginationDisplay = listStylePagination
    ? getListTablePaginationDisplay({
        pageSize,
        pageSizeTriggerLabel,
        selectedPageSizeChoice,
      })
    : null;
  const effectivePageSizeLabel = listPaginationDisplay
    ? listPaginationDisplay.triggerLabel
    : (pageSizeTriggerLabel ?? String(pageSize));
  const effectivePageSizeChoice = listPaginationDisplay
    ? listPaginationDisplay.selectedChoice
    : selectedPageSizeChoice === undefined
      ? pageSize
      : selectedPageSizeChoice;
  const normalizedTablePageSize =
    pageSizeOptions.includes(pageSize) || pageSize === 0 ? pageSize : (pageSizeOptions[0] ?? 10);

  useEffect(() => {
    if (!listStylePagination || !loadMoreEnabled || !onLoadMore) return;
    const el = loadMoreSentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) onLoadMore();
      },
      { root: null, rootMargin: "160px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [listStylePagination, loadMoreEnabled, onLoadMore, items.length]);

  const showLoadMoreSentinel =
    listStylePagination &&
    loadMoreEnabled &&
    !!onLoadMore &&
    displayItems.length > 0 &&
    !showSearchEmpty &&
    !showSkeletonBody;

  const tableRefreshOverlayStyle = isTableRefreshing
    ? { opacity: 0.72, transition: "opacity 0.2s ease" }
    : undefined;

  const useFillLayout = stickyHeader && fillViewportHeight;
  const tableStickyFirstColumn = stickyFirstColumn ?? stickyHeader;
  const tableStickyActionColumn = stickyActionColumn ?? stickyHeader;

  return (
    <div
      className={useFillLayout ? "data-table data-table--fill" : undefined}
      aria-busy={isTableRefreshing || undefined}
    >
      <ListTableScrollBoundaryRefContext.Provider value={listTableScrollBoundaryRef}>
        <ListTableScrollRegion
          ref={listTableScrollBoundaryRef}
          className={useFillLayout ? "data-table__scroll--fill" : undefined}
          style={tableRefreshOverlayStyle}
        >
          <Table
            size={size}
            showCheckbox={showCheckbox}
            onCheckbox={onCheckbox}
            stickyHeader={stickyHeader}
            stickyHeaderFill={useFillLayout}
            scrollMaxHeight={stickyHeader ? (useFillLayout ? "100%" : scrollMaxHeight) : undefined}
            stickyFirstColumn={tableStickyFirstColumn}
            stickyActionColumn={tableStickyActionColumn}
          >
            <TableHeader>
              {showCheckbox && (
                <TableHeaderCell
                  color="gray"
                  size="xxs"
                  checkbox
                  checkboxChecked={allSelected}
                  checkboxIndeterminate={someSelected}
                />
              )}
              {columns.map((col) => {
                const columnSortable = isColumnSortable(col);
                const sortArrow = columnSortable
                  ? getDataTableSortArrow(sortColumnId, col.id, sortDirection)
                  : false;
                const { onClick: headerOnClick, ...headerCellRest } = col.headerCellProps ?? {};

                return (
                  <TableHeaderCell
                    key={col.id}
                    color="gray"
                    size="xxs"
                    sortable={columnSortable}
                    arrow={columnSortable ? sortArrow : false}
                    onSort={columnSortable ? () => handleSortColumn(col.id) : undefined}
                    {...(col.actionColumn
                      ? { ...DATA_TABLE_ACTION_HEADER_CELL_PROPS, ...headerCellRest }
                      : headerCellRest)}
                    onClick={headerOnClick}
                  >
                    {col.label}
                  </TableHeaderCell>
                );
              })}
            </TableHeader>
            <TableBody>
              {showSkeletonBody ? (
                <TableSkeletonRows
                  dataColumnCount={dataColumnCount}
                  checkboxColumn={showCheckbox}
                  rowCount={skeletonRowCount}
                />
              ) : showSearchEmpty ? (
                <TableRow>
                  <TableCell
                    colSpan={colCount}
                    style={{ verticalAlign: "middle", minHeight: "40vh" }}
                  >
                    {(!hasSearchQuery || alwaysShowEmptyStateWhenNoData) && emptyStateWhenNoData
                      ? emptyStateWhenNoData
                      : (searchEmptyState ?? (
                          <EmptyState
                            title="No Matching Search Results"
                            description="Try a different search term."
                          />
                        ))}
                  </TableCell>
                </TableRow>
              ) : displayItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} />
                </TableRow>
              ) : (
                <>
                  {displayItems.map((item, index) => {
                    const id = getRowId(item);
                    return (
                      <React.Fragment key={`${String(id)}-idx-${index}`}>
                        {renderRow(item, selectedIds.has(id))}
                      </React.Fragment>
                    );
                  })}
                  {listStylePagination && isLoadingMore ? (
                    <TableRow>
                      <TableCell colSpan={colCount}>
                        <Typography
                          variant="body"
                          bodySize="sm"
                          bodyWeight="regular"
                          color="secondary"
                        >
                          Loading more…
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {showLoadMoreSentinel ? (
                    <TableRow aria-hidden>
                      <TableCell colSpan={colCount} style={{ padding: 0, border: 0, height: 1 }}>
                        <div ref={loadMoreSentinelRef} style={{ height: 1 }} />
                      </TableCell>
                    </TableRow>
                  ) : null}
                </>
              )}
            </TableBody>
          </Table>
        </ListTableScrollRegion>
        {showPagination ? (
          listStylePagination ? (
            <ListTablePagination
              page={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              pageSizeTriggerLabel={effectivePageSizeLabel}
              selectedPageSizeChoice={effectivePageSizeChoice}
              onPageChange={(p) => onPageChange(Math.max(1, p))}
              onPageSizeChange={onPageSizeChange}
              itemsRangeOverride={itemsRangeOverride}
              hidePageNavigation={hidePageNavigation}
              showScrollForMoreHint={showScrollForMoreHint}
            />
          ) : (
            <TablePagination
              page={currentPage}
              totalPages={totalPages}
              pageSize={normalizedTablePageSize}
              totalItems={totalItems}
              pageSizeOptions={pageSizeOptions}
              onPageChange={(p) => onPageChange(Math.max(1, p))}
              onPageSizeChange={onPageSizeChange}
            />
          )
        ) : reservePaginationSlot ? (
          <TablePaginationPlaceholder />
        ) : null}
      </ListTableScrollBoundaryRefContext.Provider>
    </div>
  );
}

export default DataTable;
